import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { fetchInstitutionalDashboard } from '../lib/queries';
import { PageHeader, ProgressBar, QueryState, UnitStatusPill } from '../components/ui';
import { AttentionTable, EvolutionLegend, MonthlyEvolutionChart, RiskKpis } from '../components/DashboardWidgets';
import type { UnitRatStatus } from '@rat/shared';

export function InstitutionalDashboard() {
  const q = useQuery({ queryKey: ['dash', 'inst'], queryFn: fetchInstitutionalDashboard });

  return (
    <>
      <PageHeader
        eyebrow="Responsabilidad proactiva · Ley 21.719"
        title="Tablero institucional"
        sub="Avance del Registro de Actividades de Tratamiento"
        actions={
          <Link className="btn" to="/reportes">
            Exportar reporte
          </Link>
        }
      />
      <QueryState isLoading={q.isLoading} error={q.error}>
        {q.data && (
          <>
            <div className="kpis" style={{ marginBottom: 18 }}>
              <Kpi n={q.data.unidades_total} l="Unidades / direcciones" />
              <Kpi n={q.data.actividades_total} l="Actividades" />
              <Kpi n={q.data.por_estado['EN_REVISION'] ?? 0} l="En revisión" />
              <Kpi n={q.data.por_estado['APROBADO'] ?? 0} l="Aprobadas" />
              <Kpi n={q.data.por_estado['CERRADO'] ?? 0} l="Cerradas" />
            </div>

            <div className="eyebrow" style={{ marginBottom: 8 }}>
              Riesgo y cumplimiento
            </div>
            <RiskKpis riesgo={q.data.riesgo} />

            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr)', gap: 16, marginBottom: 16 }}>
              <div className="card">
                <div className="card__h">
                  <h3 style={{ fontSize: 15 }}>Necesitan atención</h3>
                </div>
                <div className="card__b">
                  <AttentionTable rows={q.data.atencion} showUnit />
                </div>
              </div>

              <div className="card">
                <div className="card__h">
                  <h3 style={{ fontSize: 15 }}>Evolución mensual</h3>
                </div>
                <div className="card__b">
                  <MonthlyEvolutionChart data={q.data.evolucion_mensual} />
                  <EvolutionLegend />
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card__h">
                <h3 style={{ fontSize: 15 }}>Avance por unidad</h3>
              </div>
              <div className="card__b">
                <div className="tablewrap" style={{ border: 0 }}>
                  <table className="data">
                    <thead>
                      <tr>
                        <th>Unidad</th>
                        <th>Estado</th>
                        <th>Actividades</th>
                        <th>Completitud</th>
                      </tr>
                    </thead>
                    <tbody>
                      {q.data.por_unidad.map((u) => (
                        <tr key={u.unit_id}>
                          <td>
                            <Link to={`/unidad/${u.unit_id}`}>{u.name}</Link>
                          </td>
                          <td>
                            <UnitStatusPill status={u.rat_status as UnitRatStatus} />
                          </td>
                          <td className="mono">{u.activities_total}</td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <ProgressBar pct={u.completeness_avg} />
                              <span className="mono">{u.completeness_avg}%</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}
      </QueryState>
    </>
  );
}

function Kpi({ n, l }: { n: number | string; l: string }) {
  return (
    <div className="kpi">
      <div className="n">{n}</div>
      <div className="l">{l}</div>
    </div>
  );
}
